import React, {useMemo} from 'react';
import cn from 'classnames';
import {Speaker, SpeakerNumber, TopicMatch, TranscriptBlock} from '@store/call-card/CallCard.types';
import {List, Map} from 'immutable';
import {Color, Seconds} from '@store/@types/util';
import './WideTranscript.sass';
import {WideTranscriptLine} from '@components/call-card/WideTranscriptLine';
import User from 'records/User';
import {TopicId} from '@store/topic-keyword/TopicKeyword.types';
import {combineMatchesByLine} from './TranscriptMatch.utils';
import {FileUploadState, inPreTranscribedState, inErrorState} from 'records/FileUploadState';

interface WideTranscriptProps {
  transcript: List<TranscriptBlock>;
  speakers: Map<SpeakerNumber, Speaker>;
  hidden: boolean;
  sessionUser?: User;
  matches: Map<TopicId, List<TopicMatch>>;
  activeTopics: Map<TopicId, Color>;
  setTimeSeconds: (seconds: Seconds) => void;
  callState: FileUploadState;
  currentTime: Seconds;
  setTranscriptRef: (ref: HTMLElement | null) => void;
}

export const WideTranscript: React.FC<WideTranscriptProps> = ({
  transcript,
  speakers,
  sessionUser,
  matches,
  activeTopics,
  hidden,
  setTimeSeconds,
  callState,
  currentTime,
  setTranscriptRef,
}) => {
  const matchedBlocks = useMemo(
    () => combineMatchesByLine(matches, activeTopics),
    [matches, activeTopics]
  );

  if (matchedBlocks.isEmpty()) {
    return (
      <div className={cn('wide-transcript', {hidden})} ref={ref => setTranscriptRef(ref)}>
        {transcript.map(block => (
          <WideTranscriptLine
            key={block.startTime}
            onClick={() => setTimeSeconds(block.startTime)}
            chunk={block.chunk}
            speaker={speakers.get(block.speakerNumber)!}
            startTime={block.startTime}
            activeTopics={activeTopics}
            matches={List()}
            authUserId={sessionUser?.id}
            highlightLine={currentTime >= block.startTime && currentTime < block.endTime}
          />
        ))}
        {transcript.isEmpty() && inPreTranscribedState(callState) && (
          <div className="transcript-state">
            <p>We are processing this transcript.</p>
            <p>Check back shortly.</p>
          </div>
        )}
        {transcript.isEmpty() && inErrorState(callState) && (
          <div className="transcript-state">
            <p>We cannot process this transcript.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('wide-transcript', {hidden})} ref={ref => setTranscriptRef(ref)}>
      {matchedBlocks
        .map((matches, startTime) => {
          const firstMatch = matches.first();
          return (
            <WideTranscriptLine
              key={startTime}
              onClick={() => setTimeSeconds(firstMatch.startTime)}
              chunk={firstMatch.chunk}
              speaker={speakers.get(firstMatch.speakerNumber)!}
              startTime={startTime}
              activeTopics={activeTopics}
              matches={matches}
              authUserId={sessionUser?.id}
              highlightLine={
                currentTime >= firstMatch.startTime && currentTime < firstMatch.endTime
              }
            />
          );
        })
        .toList()}
    </div>
  );
};
